import { FormData } from "./FormStorageService";
import PromoCacheService from "./PromoCacheService";
import AuthService from "./AuthService";

const API_BASE_URL = process.env.GATSBY_API_URL;

class ApiService {
  static async handleResponse(response: Response) {
    if (!response.ok) {
      const error = new Error(`API call failed: ${response.statusText}`);
      // Add status code to the error object for more specific error handling
      (error as any).status = response.status;
      throw error;
    }
    return response;
  }

  static async get(endpoint: string, headers: Record<string, string> = {}) {
    try {
      // Add auth token for admin endpoints
      if (endpoint.startsWith("/admin/")) {
        const token = AuthService.getAuthToken();
        if (token) {
          headers["X-AUTH-TOKEN"] = token;
        }
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error("API call failed:", error);
      throw error;
    }
  }

  static async post(
    endpoint: string,
    data: any = null,
    headers: Record<string, string> = {},
  ) {
    try {
      // Add auth token for admin endpoints
      if (endpoint.startsWith("/admin/")) {
        const token = AuthService.getAuthToken();
        if (token) {
          headers["X-AUTH-TOKEN"] = token;
        }
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
        body: data ? JSON.stringify(data) : null,
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error("API call failed:", error);
      throw error;
    }
  }

  // Function to get cached promo code using PromoCacheService
  static getCachedPromoCode(): string | null {
    return PromoCacheService.getPromoCode();
  }

  static async submitBackgroundCheckForm(formData: FormData) {
    // Check if there's a cached promo code
    const cachedPromoCode = this.getCachedPromoCode();

    // Prepare the endpoint with query parameter if promo code exists
    let endpoint = "/payment";
    if (cachedPromoCode) {
      endpoint = `${endpoint}?promoCode=${encodeURIComponent(cachedPromoCode)}`;
    }

    const response = await this.post(endpoint, {
      productType: formData.productType,
      firstName: formData.firstName,
      middleNames: formData.middleNames,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
    });

    if (response.ok) {
      return response.json();
    }
    throw new Error("Payment submission failed");
  }
}

export default ApiService;
